import React, { useEffect, useRef } from 'react';

export interface ZIndexProps {
  /**
   * Flag to indicate if or not to assign z-index.
   *
   * @default false
   */
  assignZIndex?: boolean;
  /**
   * Children node may be actual components or portal that the ZIndex component
   * would wrap.
   */
  children: React.ReactElement;
  /**
   * Flag to indicate if or not we need to disabled zIndex or if component wants to
   * handle zIndex by itself.
   *
   * @default false
   */
  disableZIndex?: boolean;
  /**
   * Style object to cascade
   *
   * @default {}
   */
  style?: React.CSSProperties;
  /**
   * Method to increment topZIndex for the component. ZIndex would call this method
   * to update when a component needs to be mounted and needed higher zIndex.
   */
  incrementBaseZIndex?: () => void;
  /**
   * Method to decrement topZIndex for the component. ZIndex would call this method
   * to update when a component is unmounted and we need to reset the zIndex applied.
   */
  decrementBaseZIndex?: () => void;
  /**
   * Getter method to get topZIndex for the component from the Context.
   */
  getCurrentBaseZIndex?: () => number;
}

/**
 * ZIndex component to manage  ui stacking context
 *
 * @version PACKAGE_VERSION
 * @tutorial http://in/ids-storybook
 */
export const ZIndexBase = ({
  assignZIndex = false,
  children,
  decrementBaseZIndex,
  disableZIndex = false,
  incrementBaseZIndex,
  getCurrentBaseZIndex,
  style = {}
}: ZIndexProps) => {
  const childZIndex = useRef(0);

  useEffect(() => {
    if (
      !assignZIndex &&
      !disableZIndex &&
      childZIndex.current > 0 &&
      decrementBaseZIndex
    ) {
      decrementBaseZIndex();
      childZIndex.current--;
    }
  }, [assignZIndex, decrementBaseZIndex, disableZIndex]);

  /**
   * Method to copy and cascade css styles and classes to the child.
   *
   * @param pCurrentBaseZIndex - zIndex to be applied on child style.
   */
  const copyAndCascadeStylesForChildren = (pCurrentBaseZIndex: number) => {
    let updatedChildren = null;
    // eslint-disable-next-line no-negated-condition
    if (!disableZIndex) {
      updatedChildren = React.Children.map(
        children,
        (child: React.ReactElement) =>
          React.cloneElement(child, {
            style: {
              ...child.props.style,
              ...style,
              zIndex: pCurrentBaseZIndex
            }
          })
      );
    } else {
      updatedChildren = React.cloneElement(children);
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{updatedChildren}</>;
  };

  /**
   * Sub-render method to render children, when we have to update zIndex for components with portal.
   */
  const renderChildrenWithZIndexWhenPortalShown = () => {
    let updatedChildren = null;
    if (childZIndex.current >= 0) {
      if (!disableZIndex) {
        incrementBaseZIndex && incrementBaseZIndex();
        childZIndex.current++;
      }
    }

    if (getCurrentBaseZIndex) {
      updatedChildren = copyAndCascadeStylesForChildren(getCurrentBaseZIndex());
    }

    return updatedChildren;
  };

  /**
   * Sub-render method to render children, when we have to update zIndex for components with no portal.
   */
  const renderChildrenWithZIndexWhenPortalNotShown = () => {
    let updatedChildren = null;
    if (childZIndex.current >= 0) {
      if (!disableZIndex) {
        incrementBaseZIndex && incrementBaseZIndex();
        childZIndex.current++;
      }
    }

    if (getCurrentBaseZIndex) {
      updatedChildren = copyAndCascadeStylesForChildren(getCurrentBaseZIndex());
    }

    return updatedChildren;
  };

  return assignZIndex
    ? renderChildrenWithZIndexWhenPortalShown()
    : renderChildrenWithZIndexWhenPortalNotShown();
};

ZIndexBase.displayName = 'ZIndex';

export default ZIndexBase;
